import { useState, useCallback } from "react"
import { Button } from "~/react/ui/button"
import { Icon } from "~/react/ui/icon"
import { Popover, PopoverContent, PopoverTrigger } from "~/react/ui/popover"
import { Tooltip, TooltipContent, TooltipTrigger } from "~/react/ui/tooltip"
import { sendTestEmail } from "../api"
import { t } from "../i18n"
import { Input } from "@/react/ui/input"

// Declare App as global for flash messages
declare const App: {
  displayFlash: (type: string, message: string) => void
}

interface SendTestEmailProps {
  campaignId: number
  step: number
  defaultEmail: string
  disabled?: boolean
}

export function SendTestEmail({
  campaignId,
  step,
  defaultEmail,
  disabled = false,
}: SendTestEmailProps) {
  const [open, setOpen] = useState(false)
  const [email, setEmail] = useState(defaultEmail)
  const [sending, setSending] = useState(false)

  const handleSend = useCallback(async () => {
    if (!email.trim() || sending) return

    setSending(true)

    const result = await sendTestEmail({ campaignId, step, email })

    setSending(false)

    if (result.success) {
      setOpen(false)
      App.displayFlash("success", result.message)
    } else {
      App.displayFlash("danger", result.message)
    }
  }, [campaignId, step, email, sending])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault()
      handleSend()
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="sm" disabled={disabled}>
              <Icon icon="paper-plane" variant="regular" aria-hidden="true" />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent>
          {t("js.campaigns.content.send_test_email.button_tooltip")}
        </TooltipContent>
      </Tooltip>
      <PopoverContent
        side="bottom"
        align="end"
        className="dropdown-menu dropdown-menu--wide"
        style={{ right: 0, left: "auto" }}
      >
        <div className="m-2 mb-4">
          <label className="h-label" htmlFor="send-test-recipient">
            {t("js.campaigns.content.send_test_email.input_label")}
          </label>
          <Input
            id="send-test-recipient"
            size="sm"
            fullWidth
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={sending}
          />
        </div>
        <Button
          fullWidth
          size="sm"
          className="dropdown-menu__submit"
          onClick={handleSend}
          disabled={sending || !email.trim()}
        >
          {sending
            ? t("js.campaigns.content.send_test_email.sending")
            : t("js.campaigns.content.send_test_email.send_button")}
        </Button>
      </PopoverContent>
    </Popover>
  )
}
